/**
 * StatementPreviewDemo — landing-page animation showing the
 * "drag a CSV → see what we detected before we touch your data"
 * confirm-modal flow. Pure CSS / React state, no backend calls.
 *
 * Scenes (auto-advance every ~3s, with Replay control):
 *   1. Empty dropzone with hint copy
 *   2. CSV file dragged in (file chip appears, dropzone glows)
 *   3. Preview modal slides up — detected columns + first rows
 *   4. Confirm pulse → "Parsing… 12,408 transactions" success state
 */
import { useEffect, useState } from "react";
import { CheckCircle2, FileSpreadsheet, Sparkles, UploadCloud, X, RotateCcw, AlertCircle } from "lucide-react";

const SCENES = [
  { key: "idle", ms: 2200 },
  { key: "dropped", ms: 1600 },
  { key: "preview", ms: 4200 },
  { key: "parsed", ms: 2800 },
];

const SAMPLE_COLUMNS = ["Posted Date", "Narration", "Amount", "Balance"];
const SAMPLE_ROWS = [
  ["01/03/2024", "ACH DEPOSIT · CLIENT A", "+$2,500.00", "$12,500.00"],
  ["01/05/2024", "OFFICE RENT · ACME PROP", "−$1,800.00", "$10,700.00"],
  ["01/07/2024", "STRIPE PAYOUT", "+$3,420.55", "$14,120.55"],
  ["01/12/2024", "PAYROLL RUN", "−$6,200.00", "$7,920.55"],
];

const DETECTED = [
  { label: "Date", value: "Posted Date" },
  { label: "Description", value: "Narration" },
  { label: "Amount", value: "Amount" },
  { label: "Balance", value: "Balance" },
];

export default function StatementPreviewDemo() {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const scene = SCENES[sceneIdx].key;

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => {
      setSceneIdx((i) => (i + 1) % SCENES.length);
    }, SCENES[sceneIdx].ms);
    return () => clearTimeout(t);
  }, [sceneIdx, paused]);

  const replay = () => {
    setPaused(false);
    setSceneIdx(0);
  };

  return (
    <section
      className="border-b py-20"
      style={{ borderColor: "var(--ll-border)", background: "linear-gradient(180deg, var(--ll-bg-card) 0%, var(--ll-mint) 100%)" }}
      data-testid="statement-preview-demo-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Copy column */}
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--ll-ink-soft)] mb-3 flex items-center gap-2">
              <Sparkles size={12} /> See your data first
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              We show our work,
              <br />
              <span className="italic text-[var(--ll-ochre)]">before</span> we parse a row.
            </h2>
            <p className="mt-5 text-base text-black/80 max-w-md">
              Drag in any CSV or Excel statement and Financials Ready instantly previews what it detected — column mapping, sample rows, even quirky <span className="font-mono-fin">Posted Date</span> headers from your bank. Confirm or cancel before a single transaction touches your ledger.
            </p>

            <ul className="mt-6 space-y-2.5 text-sm">
              {[
                "Auto-maps Date · Description · Amount · Balance",
                "Surfaces warnings when a required column is missing",
                "Cancels cleanly — nothing persisted until you confirm",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2 text-black/80">
                  <CheckCircle2 size={16} className="text-[var(--ll-ink)] shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>

            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={replay}
                data-testid="preview-demo-replay"
                className="text-xs uppercase tracking-widest px-3 py-2 border bg-white hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <RotateCcw size={12} /> Replay
              </button>
              <button
                onClick={() => setPaused((p) => !p)}
                data-testid="preview-demo-pause"
                className="text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]"
              >
                {paused ? "Resume" : "Pause"}
              </button>
              <div className="ml-auto flex gap-1.5">
                {SCENES.map((s, i) => (
                  <button
                    key={s.key}
                    onClick={() => { setPaused(true); setSceneIdx(i); }}
                    data-testid={`preview-demo-scene-${s.key}`}
                    className="w-1.5 h-1.5 rounded-full transition-all"
                    style={{
                      background: i === sceneIdx ? "var(--ll-ink)" : "var(--ll-border)",
                      transform: i === sceneIdx ? "scale(1.4)" : "scale(1)",
                    }}
                    aria-label={`Scene ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Animation column — browser-chrome mock */}
          <div className="lg:col-span-7">
            <div
              className="bg-white border shadow-xl overflow-hidden relative"
              style={{ borderColor: "var(--ll-border)", aspectRatio: "16 / 11" }}
              data-testid="preview-demo-stage"
            >
              {/* Browser chrome */}
              <div className="flex items-center gap-1.5 px-3 py-2 border-b bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-border)" }}>
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                <div className="ml-3 text-[10px] font-mono-fin tracking-widest text-[var(--ll-ink-soft)] truncate">
                  financialsready.app/app/statements
                </div>
              </div>

              {/* Stage body */}
              <div className="relative h-[calc(100%-32px)] bg-[var(--ll-bg-card)] overflow-hidden">
                {/* Sidebar hint */}
                <div className="absolute left-0 top-0 bottom-0 w-12 border-r bg-white hidden sm:block" style={{ borderColor: "var(--ll-border)" }}>
                  <div className="mx-auto mt-3 w-6 h-6 grid place-items-center text-white font-display font-bold text-xs" style={{ background: "var(--ll-ink)" }}>L</div>
                </div>

                {/* Dropzone */}
                <div className="absolute inset-0 sm:left-12 p-6 flex flex-col">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Upload statement</div>
                  <div className="font-display font-bold text-lg mt-0.5 mb-4">Statements</div>

                  <div
                    className="flex-1 border-2 border-dashed grid place-items-center transition-all duration-500 relative"
                    style={{
                      borderColor: scene === "dropped" || scene === "preview" || scene === "parsed" ? "var(--ll-ink)" : "var(--ll-border)",
                      background: scene === "dropped" ? "var(--ll-mint)" : "transparent",
                    }}
                    data-testid={`preview-demo-dropzone-${scene}`}
                  >
                    {scene === "idle" && (
                      <div className="text-center px-4">
                        <UploadCloud size={32} className="mx-auto text-[var(--ll-ink-muted)] animate-bounce" style={{ animationDuration: "2.4s" }} />
                        <div className="mt-3 text-sm text-[var(--ll-ink-soft)]">Drag &amp; drop a CSV or Excel file</div>
                        <div className="text-xs text-[var(--ll-ink-muted)] mt-1 font-mono-fin">max 20 MB · PDF supported</div>
                      </div>
                    )}

                    {(scene === "dropped" || scene === "preview" || scene === "parsed") && (
                      <div
                        className="flex items-center gap-3 px-4 py-3 bg-white border shadow-sm"
                        style={{ borderColor: "var(--ll-ink)", animation: "preview-pop 0.4s ease-out" }}
                      >
                        <FileSpreadsheet size={22} className="text-[var(--ll-ink)]" />
                        <div className="text-left">
                          <div className="text-sm font-medium font-mono-fin">first_demo_bank_jan_2024.csv</div>
                          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">CSV · 14.2 KB · 4 sample rows</div>
                        </div>
                        {scene === "parsed" && (
                          <div className="ml-2 flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--ll-success)]">
                            <CheckCircle2 size={12} /> Parsed
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Preview modal overlay */}
                <div
                  className="absolute inset-0 transition-all duration-500 pointer-events-none"
                  style={{
                    background: scene === "preview" ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0)",
                  }}
                >
                  <div
                    className="absolute left-1/2 -translate-x-1/2 bg-white border shadow-2xl overflow-hidden"
                    style={{
                      borderColor: "var(--ll-border)",
                      width: "min(92%, 540px)",
                      bottom: scene === "preview" ? "8%" : "-60%",
                      transition: "bottom 0.55s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.4s",
                      opacity: scene === "preview" ? 1 : 0,
                    }}
                    data-testid="preview-demo-modal"
                  >
                    {/* Modal head */}
                    <div className="px-4 py-3 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--ll-border)" }}>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Preview · confirm columns</div>
                        <div className="font-display font-bold text-base mt-0.5">Statement preview</div>
                      </div>
                      <X size={14} className="text-[var(--ll-ink-muted)] mt-0.5" />
                    </div>

                    {/* Detected columns */}
                    <div className="px-4 pt-3 pb-2 grid grid-cols-4 gap-1.5 text-[10px]">
                      {DETECTED.map((d, i) => (
                        <div
                          key={d.label}
                          className="border px-2 py-1.5"
                          style={{
                            borderColor: "var(--ll-border)",
                            animation: scene === "preview" ? `preview-tag 0.4s ${i * 90}ms ease-out backwards` : "none",
                          }}
                        >
                          <div className="uppercase tracking-widest text-[var(--ll-ink-muted)] text-[8px]">{d.label}</div>
                          <div className="font-mono-fin text-[10px] text-[var(--ll-ink)] truncate">{d.value}</div>
                        </div>
                      ))}
                    </div>

                    {/* Sample table */}
                    <div className="px-4 pb-3">
                      <div className="border overflow-hidden" style={{ borderColor: "var(--ll-border)" }}>
                        <table className="w-full text-[9px]">
                          <thead>
                            <tr className="bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
                              {SAMPLE_COLUMNS.map((c) => (
                                <th key={c} className="px-2 py-1 text-left uppercase tracking-widest font-medium text-[8px]">{c}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {SAMPLE_ROWS.map((row, i) => (
                              <tr
                                key={i}
                                className="border-b"
                                style={{
                                  borderColor: "var(--ll-border)",
                                  animation: scene === "preview" ? `preview-row 0.35s ${250 + i * 80}ms ease-out backwards` : "none",
                                }}
                              >
                                {row.map((cell, ci) => (
                                  <td
                                    key={ci}
                                    className="px-2 py-1 font-mono-fin whitespace-nowrap"
                                    style={{
                                      color: ci === 2 && cell.startsWith("−") ? "var(--ll-error)" :
                                             ci === 2 && cell.startsWith("+") ? "var(--ll-success)" :
                                             "var(--ll-ink)",
                                    }}
                                  >
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Modal footer */}
                    <div className="px-4 py-3 border-t flex items-center justify-end gap-2 bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }}>
                      <div className="text-[9px] uppercase tracking-widest text-[var(--ll-ink-soft)] mr-auto flex items-center gap-1">
                        <AlertCircle size={10} /> 0 warnings
                      </div>
                      <span className="text-[10px] px-2.5 py-1.5 border" style={{ borderColor: "var(--ll-border)" }}>Cancel</span>
                      <span
                        className="text-[10px] px-2.5 py-1.5 text-white flex items-center gap-1"
                        style={{
                          background: "var(--ll-ink)",
                          boxShadow: scene === "preview" ? "0 0 0 0 rgba(27, 67, 50, 0.45)" : "none",
                          animation: scene === "preview" ? "preview-cta-pulse 1.6s 1.6s ease-out infinite" : "none",
                        }}
                      >
                        <CheckCircle2 size={11} /> Confirm &amp; parse
                      </span>
                    </div>
                  </div>
                </div>

                {/* Parsed success ribbon */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 px-4 py-2 text-white shadow-lg flex items-center gap-2 transition-all duration-500"
                  style={{
                    background: "var(--ll-success)",
                    bottom: scene === "parsed" ? "12%" : "-15%",
                    opacity: scene === "parsed" ? 1 : 0,
                  }}
                  data-testid="preview-demo-success-toast"
                >
                  <CheckCircle2 size={16} />
                  <div className="text-xs">
                    <div className="font-medium">Statement parsed</div>
                    <div className="text-[10px] uppercase tracking-widest opacity-90 font-mono-fin">4 transactions · AI-categorized</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Caption */}
            <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-soft)] text-center font-mono-fin">
              Animated mockup · no actual upload required
            </div>
          </div>
        </div>
      </div>

      {/* Local keyframes (scoped via unique names) */}
      <style>{`
        @keyframes preview-pop {
          0%   { transform: translateY(12px) scale(0.92); opacity: 0; }
          100% { transform: translateY(0) scale(1);       opacity: 1; }
        }
        @keyframes preview-tag {
          0%   { transform: translateY(6px); opacity: 0; }
          100% { transform: translateY(0);   opacity: 1; }
        }
        @keyframes preview-row {
          0%   { transform: translateX(-8px); opacity: 0; }
          100% { transform: translateX(0);    opacity: 1; }
        }
        @keyframes preview-cta-pulse {
          0%   { box-shadow: 0 0 0 0    rgba(27, 67, 50, 0.55); }
          70%  { box-shadow: 0 0 0 12px rgba(27, 67, 50, 0);    }
          100% { box-shadow: 0 0 0 0    rgba(27, 67, 50, 0);    }
        }
      `}</style>
    </section>
  );
}
